/**
 * Shorthand function for DOMContentLoaded (or if already loaded executes callback immediately)
 */
export const domReady = (callback: () => void): void => {
    if (document.readyState === 'complete' ||
        document.readyState !== 'loading')
        callback();
    else
        document.addEventListener('DOMContentLoaded', callback);
}


/**
* Easy selector helper function
*/
export const select = (el, all = false, container: HTMLElement | Document = document) => {
    el = el.trim()
    if (all) {
        return [...container.querySelectorAll(el)]
    } else {
        return container.querySelector(el)
    }
}

/**
 * Easy event listener function
 */
export const on = (type: keyof DocumentEventMap, el, listener: (this: Document, ev: Event) => any, all: boolean = false) => {
    if (all) {
        select(el, all).forEach(e => e.addEventListener(type, listener))
    } else {
        select(el, all).addEventListener(type, listener)
    }
}

/**
 * Easy update html element value function
 * @param el = element selector
 * @param value = value to update the matched element(s)
 * @param all = update all found elements or only the first
 */
export const update = (el, value, all = false) => {
    let els = select(el, all);
    if (!els) return;

    if (!all) els = [els];

    for (var i = 0; i < els.length; i++) {

        const htmlEl: HTMLElement = els[i];

        if ((['INPUT', 'SELECT', 'TEXTAREA'].indexOf(htmlEl.tagName)) > -1) {
            if (htmlEl.tagName === 'INPUT' && ['checkbox', 'radio'].indexOf((htmlEl as HTMLInputElement).type) > -1) {
                (els[i] as HTMLInputElement).checked = value == false || value == '' || value == null || value == undefined || value == 0 ? false : true;
            }
            els[i].value = value;
        }
    }
}

export const display = (selector: string, show: boolean) => {

    const els = [...document.querySelectorAll(selector)];
    for (var i = 0; i < els.length; i++) {
        const el = els[i];
        if (show) {
            el.classList.remove('d-none');
        } else {
            el.classList.add('d-none');
        }
    }
}

export const setChildren = (el: HTMLElement, html: string | HTMLElement) => {
    [...el.childNodes].forEach(e => e.remove());

    if (typeof html === "string")
        el.insertAdjacentHTML('afterbegin', html);
    else
        el.insertAdjacentElement('afterbegin', html);
}

export interface IOptionItem {
    value: string,
    text: string,
    selected?: boolean,
    disabled?: boolean
}

export const toOptionItemArray = (source: any, textProp: string, valueProp: string, selectedProp?: string, disabledProp?: string) => {
    if (!Array.isArray(source) || source.length === 0) return null;
    const oiArray: Array<IOptionItem> = [];
    for (var i = 0; i < source.length; i++) {
        const oi: IOptionItem = {
            value: source[i][valueProp],
            text: source[i][textProp],
        }
        if(selectedProp) oi.selected = source[i][selectedProp]
        if (disabledProp) oi.disabled = source[i][disabledProp]

        oiArray.push(oi);
    }

    return oiArray;
}

export const loadSelectOptions = (parent: HTMLSelectElement, items: Array<IOptionItem>, clear: boolean = true) => {
    if (clear) {
        while (parent.options.length > 1) {
            parent.options.remove(1)
        }
    }

    if (!Array.isArray(items) || items.length == 0) return;

    for (var i = 0; i < items.length; i++) {
        const opt = document.createElement('option');
        opt.value = items[i].value;
        opt.text = items[i].text;
        if (items[i].selected) opt.selected = items[i].selected;
        if (items[i].disabled) opt.disabled = items[i].disabled;

        parent.options.add(opt);
    }
}

export const isElementOfType = (el: HTMLElement, type: string | string[]): boolean => {

    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLSelectElement) && !(el instanceof HTMLTextAreaElement)) return false;

    if (!(type instanceof Array)) type = [type];

    return type.indexOf(el.type) > -1;
}

/**
 * Escape special characters within a string to be used for a regular expression
 * @param string
 * @returns
 */
const escapeRegExp = string => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const serialize = (container: string | HTMLElement, trimPrefix: string = null) => {

    let fobj = ((typeof container == 'string' ? select(container) : container) as HTMLElement);
    let rx;

    if (trimPrefix) {
        rx = new RegExp(`^${escapeRegExp(trimPrefix)}`);
    }

    // get an iterable array of arrays, [ [key,value], [key,value]..]
    // of all input/select/textarea within container that have a name attribute
    //const fdata = [...fobj.querySelectorAll('input[name], select[name], textarea[name]')].map((el: HTMLElement) => {
    const fdata = [...fobj.querySelectorAll('input[name], select[name], textarea[name]')].flatMap((el: HTMLElement) => {

        if (isElementOfType(el, ['checkbox', 'radio']) && !(el as any).checked) return []

        //@ts-ignore
        //let elval = isElementOfType(el, ['checkbox', 'radio']) ? el.checked : el.value;
        let elval = el.value;

        //@ts-ignore
        let elname = trimPrefix ? el.name.replace(rx, '') : el.name;

        //return [elname, elval];
        return [[elname, elval]];
    });

    let obj = {};

    for (let [key, value] of fdata) {
        if (obj[key] !== undefined) {
            if (!Array.isArray(obj[key])) {
                obj[key] = [obj[key]];
            }
            obj[key].push(value);
        } else {
            obj[key] = value;
        }
    }
    return obj;
}

export const serializeAsQueryString = (container: string | HTMLElement) => {

    if (!(container instanceof HTMLElement) && typeof container != 'string')// && typeof container != 'object')
        throw new Error('container must be HTMLElement | string');// | object');

    //if (typeof container != 'object')
    let searchPrams = serialize(container);

    return new URLSearchParams(searchPrams).toString();

    //let fobj = ((typeof container == 'string' ? select(container) : container) as HTMLElement);

    //const formData = new FormData();

    //[...fobj.querySelectorAll('input[name], select[name], textarea[name]')].forEach((el: HTMLElement) => {
    //    //@ts-ignore
    //    formData.append(el.name, el.value);
    //});
    //// @ts-ignore
    //const ret2 = new URLSearchParams(formData).toString();

    //return ret2;
}

// credit: https://gist.github.com/tjmehta/9204891?permalink_comment_id=3527084#gistcomment-3527084
export const objectToQueryString = (initialObj) => {
    const reducer = (obj, parentPrefix = null) => (prev, key) => {
        const val = obj[key];
        key = encodeURIComponent(key);
        const prefix = parentPrefix ? `${parentPrefix}[${key}]` : key;

        if (val == null || typeof val === 'function') {
            prev.push(`${prefix}=`);
            return prev;
        }

        if (['number', 'boolean', 'string'].includes(typeof val)) {
            prev.push(`${prefix}=${encodeURIComponent(val)}`);
            return prev;
        }

        prev.push(Object.keys(val).reduce(reducer(val, prefix), []).join('&'));
        return prev;
    };

    return Object.keys(initialObj).reduce(reducer(initialObj), []).join('&');
};

export const getCheckboxHiddenExtraInputs = (container: string | HTMLElement | Document = document) => {

    if (typeof container == 'string') container = document.querySelector(container) as HTMLElement;

    const cbs = container.querySelectorAll('input[name][type="checkbox"][value="true"]');
    return [...cbs].map((cb: HTMLInputElement) => {
        return container.querySelector(`input[name="${cb.name}"][type="hidden"][value="false"]`) as HTMLElement;
    })
}

export const moveElements = (elements: string | HTMLElement[], newParent: string | HTMLElement, position: InsertPosition) => {
    if (typeof elements == 'string') elements = select(elements, true) as HTMLElement[];

    if (typeof newParent == 'string') newParent = select(newParent) as HTMLElement;

    for (var i = 0; i < elements.length; i++) {
        newParent.insertAdjacentElement(position, elements[i]);
    }
}

export const toHtmlDom = (htmlString: string) => {
    return new DOMParser().parseFromString(htmlString, 'text/html').body.firstChild as HTMLElement;
}

export function fakeLinkClick(href, target, method = 'GET', data = null) {

    if (method.toUpperCase() !== 'GET') throw new Error(`${method} no implemented`);

    target = target || "_blank";

    //let el = document.createElement(method.toUpperCase() == 'GET' ? 'a' : 'form');
    let el = document.createElement('a');
    el.target = target;

    if (el.tagName == 'A') {
        el.href = href;
        document.body.appendChild(el);
        el.click();
    }
    //else {
    //    //debugger;
    //    el.action = href;
    //    el.method = method;
    //    addObjectAsFormInputs(el, null, data);
    //    document.body.appendChild(el);
    //    el.submit();
    //}

    setTimeout(function () {
        document.body.removeChild(el);
    }, 100);
}

export function isVisible(elem) { return elem.offsetWidth > 0 || elem.offsetHeight > 0 || elem.getClientRects().length > 0; }

export function findFirstVisible(elements: HTMLElement[]) : HTMLElement {
    for (var i = 0; i < elements.length; i++) {
        const el = elements[i]
        if (isVisible(el)) return el ;
    }
}
export function isInViewport(ele, offsetRect: DOMRect = new DOMRect(0, 0, 0, 0)) {
    const rect = ele.getBoundingClientRect();
    rect.y += offsetRect.y;
    rect.x += offsetRect.x;
    rect.height += offsetRect.height;
    rect.width += offsetRect.width;

    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
};

export function isVisibleInContainer(ele, container, offsetRect: DOMRect = new DOMRect(0, 0, 0, 0)) {
    const eleTop = ele.offsetTop;
    const eleBottom = eleTop + ele.clientHeight;

    const containerTop = container.scrollTop + offsetRect.top;
    const containerBottom = containerTop + container.clientHeight - offsetRect.height;

    // The element is fully visible in the container
    return (
        (eleTop >= containerTop && eleBottom <= containerBottom) ||
        // Some part of the element is visible in the container
        (eleTop < containerTop && containerTop < eleBottom) ||
        (eleTop < containerBottom && containerBottom < eleBottom)
    );
};